// Core
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Route, Switch, Redirect, useRouteMatch, useHistory, NavLink, Link, useLocation } from 'react-router-dom';
import {
  Button,
  Box,
  Flex,
  Grid,
  Text,
  Image,
  Stack,
  Skeleton,
  Checkbox,
  useColorMode,
  useColorModeValue,
  useDisclosure
} from '@chakra-ui/react';

// Accets
import { ReactComponent as EmptyListNotifications } from 'assets/svg/empty-list-notifications.svg';
import { apiService } from 'services/ApiService';
import { formatDateWithSuffix, colorUserSubBg, validateLoadMore } from 'services/Utils';
import profileSub from 'assets/img/profile-sub.png';
import emptyPlan from 'assets/img/empty-plan.png';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';

// Components
import Card from 'components/Card/Card';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import AttentionModal from 'components/Modals/AttentionModal';
import ErrorCard from 'components/Form/Errors';
import FilterSelect from 'components/Form/FilterSelect';
import CancelSubscriptionModal from './CancelSubscription';

const Subscriptions = ({ userData, handleLogout }) => {
	const bg = useColorModeValue('white', '#0A1027');
	const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
	const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');
	const styleStatus = {
		height: '22px',
  	borderRadius: '100px',
		padding: '2px 12px 2px 10px',
  	bg: 'linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)', 
  	fontSize: '15px',
  	color: 'white',
  	fontWeight: '700',
  	fontStyle: 'italic',
		letterSpacing: '-1px',
		textTransform: 'uppercase'
	};

	const { colorMode } = useColorMode();
	const isDark = colorMode === 'dark';
	
	const history = useHistory();
	const location = useLocation();

	const [offset, setOffset] = useState(0);
	const [isLoadingList, setIsLoadingList] = useState(false);
	const [activePlans, setActivePlans] = useState([]);
	const [activePlansMeta, setActivePlansMeta] = useState();
	const limit = 24;

	const searchParams = new URLSearchParams(location.search);
	const statusUrl = searchParams.get('status');
	const status = !statusUrl || statusUrl === 'all' ? '' : statusUrl;

	const { isLoading } = useQuery({
	  queryKey: ['active-plans', status, offset],
	  queryFn: async () => {
	    const params = { limit, offset };
	    if (status) {
	      params['status'] = status;
	    }

	    const response = await apiService.getSubscriptions(params);

	    if (offset === 0) {
	      setActivePlans(response.data);
	    } else {
	      setActivePlans((prev) => [...prev, ...response.data]);
	    }

	    setActivePlansMeta(response.meta);
	    setIsLoadingList(false);

	    return true;
	  },
	  refetchOnWindowFocus: false,
	  retry: false,
	});

	const loadMore = async () => {
	  setOffset((prev) => prev + limit);
	};

	const handleStatus = (e) => {
	  const newStatus = e.target.value;

	  setActivePlans([]);
	  setOffset(0);
	  setIsLoadingList(true);

	  const params = new URLSearchParams(location.search);

	  if (!newStatus || newStatus === 'all') {
	    params.delete('status'); 
	  } else {
	    params.set('status', newStatus);
	  }

	  history.push(`${location.pathname}?${params.toString()}`);
	};

  const [isLoadingCustomerPortal, setIsLoadingCustomerPortal] = useState(false);
  const goToCustomerPortal = useCallback(async (id) => {
    setIsLoadingCustomerPortal(true);
    const response = await apiService.goToSubscriptionCustomerPortal({id});

    if (response.message === 'Success') {
      window.location.href = response.data;
    } 

    setIsLoadingCustomerPortal(false);
  }, []);

	const [errorCard, setErrorCard] = useState();
	const [changeStatusSubItem, setChangeStatusSubItem] = useState();
	const [isLoadingChangeStatusSub, setIsLoadingChangeStatusSub] = useState(false);
	const { isOpen, onClose, onOpen } = useDisclosure();
	const changeStatusSub = async () => {
	  setIsLoadingChangeStatusSub(true);

	  if(changeStatusSubItem.status !== 'active' 
	  	&& changeStatusSubItem.status !== 'paused'){
	  	return;
	  }  
	  
	  const data = {
	  	id: changeStatusSubItem.subscriptionId,
	  	status: changeStatusSubItem.status === 'active' ? 'pause' : 'active'
	  };

	  try {
	  	const response = await apiService.changeStatusSubscription(data);

	  	if(response.statusCode !== 200){
				setErrorCard(response.message);
				setIsLoadingChangeStatusSub(false);
				return;
			}

	  	const cleanUrl = window.location.origin + window.location.pathname;
			window.location.href = cleanUrl;
	  } catch (e){
	  	setErrorCard(true);
	  }
	  
	  setIsLoadingChangeStatusSub(false);
	};

	const { isOpen: isOpenCancelAttention, onClose: onCloseCancelAttention, onOpen: onOpenCancelAttention } = useDisclosure();
	const handleCancelMembership = async () => {
    onOpenCancelAttention();
  };

  /*const handleCancelMembership = async () => {
  	try {
        await apiService.unSubscription({id: changeStatusSubItem?.id});
    } catch (error) {
        console.error('Error:', error);
    } 

    const cleanUrl = window.location.origin + window.location.pathname;
		window.location.href = cleanUrl;
  };*/

	const { isOpen: isOpenAttention, onClose: onCloseAttention, onOpen: onOpenAttention } = useDisclosure();
  const confirmHandleCancelMembership = async ({reason}) => {
  	try {
      await apiService.unSubscriptionWithReason({
      	id: changeStatusSubItem?.id,
      	reason
      });
    } catch (error) {
      console.error('Error:', error);
    } 

    /*if(activePlans?.length === 1 && (status === 'active' || !status)){
    	handleLogout();
    } else {*/
	    const cleanUrl = window.location.origin + window.location.pathname;
			window.location.href = cleanUrl;
		//}
  };

	return (
		<>
			<Flex align='left' mb={{ base: '10px', md: '16px' }}
        direction={{ base: 'row' }}
        w={{ sm: '100%' }}
        textAlign={{ base: 'center', md: 'start' }}
        borderBottom="1px"
        className="membership-tabs">
        <Link to="/account/membership">Active Plan</Link>
        <Link to="/account/membership/subscriptions" className="membership-tab-active">Manage Subscriptions</Link>
      </Flex>

	    <FilterSelect 
	    	id="filter-select"
	    	onChange={handleStatus}
	    	value={status}
	    	w={{base: '100%', md: '164px'}}
	    	options={[
			    { value: '', title: 'Show All' },
			    { value: 'active', title: 'Active' },
			    { value: 'paused', title: 'Paused' },
			    { value: 'not-active', title: 'Inactive' }
			  ]}
	    />

	    { errorCard && ( <ErrorCard mt={{base: '-8px'}} mb={{base: '16px', md: '8px'}} error={errorCard} /> )}

			<Grid
        templateColumns={{ sm: '1fr', lg: '1fr 1fr' }}
        templateRows="1fr"
        pb="8px"
        pt="8px"
        mt={{ base: '-16px', md: '-8px'}}
        gap="16px">
        { ((isLoading && activePlans?.length === 0) || (isLoadingList && offset === 0)) && 
	        <>
	        	<Skeleton height="240px" borderRadius="16px" mb="24px" />
	        	<Skeleton height="240px" borderRadius="16px" mb="24px" />
	        </> 
	      }

        {activePlans && activePlans?.map((row, index) => (
        <Card 
        	key={index} 
        	padding="16px 12px"
        	bg={row?.canceledAt === null && row?.periodEnd ? 'initial' : isDark ? '#262B40' : '#CCD7F3'} 
        	overflow="hidden">

        	{ isDark && 
	        	<Box 
	        		w="100%"
	        		h="100px"
	        		borderRadius="full"
							bg="#099BFF"
							filter="blur(220px)"
							position="absolute"
							top="-100%"
							left="0px"
	        	/>
	        }

        	{ isDark ? 
        		<Lines className="abstract-lines" style={{zIndex: -1, transform: 'scale(1.8)', opacity: 0.3}} /> 
        		: 
        		<LinesWhite className="abstract-lines" style={{zIndex: -1, opacity: 0.3}} />
        	}

        	<Image 
						zIndex="3"
						opacity="0.2"
						src={row?.plan?.image || emptyPlan} alt="Profile" 
						right="-15%" top="-20%" objectFit="cover" position="absolute" w="300px" h="300px"/>

					<Flex w="100%" justifyContent="space-between" zIndex="9">
						<Grid gap="12px" zIndex="1" position="relative" zIndex="3" w="100%" alignItems="center" templateColumns={{ base: '44px 1fr', md: '64px 1fr' }}>
							<Box
							  p={{base: '4px', sm: '10px'}}
							  bg={() => colorUserSubBg(row?.plan?.type)}
							  boxShadow="0px 2.56px 9.344px 0px rgba(0, 0, 0, 0.25)"
							  borderRadius="12px">
							  <Image 
							  	maxW="100%"
							  	src={row?.plan?.image || emptyPlan} 
							  	alt="Plan" />
							</Box>

							<Text 
								variant="title-lg" 
								maxW={{base: '70%', md: '200px'}}
								mt="-2px" 
								fontStyle="normal"
								fontSize={{base: '16px', sm: '18px', md: '20px' }} 
								fontWeight="800">
							    {row?.plan?.name}
							</Text>
						</Grid>

						{ (row?.status === 'active' || row?.status === 'trialing') ? (
						  <Text style={styleStatus} bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)">Active</Text>
						) : row?.status === 'paused' ? (
							<Text style={styleStatus} bg="#6B7795">Paused</Text>
						) : 
							<Text style={styleStatus} bg="#6B7795">Expired</Text>
						}
					</Flex>

					<Box zIndex="3" position="relative" mt="8px">
			    	<Text variant="title-lg" fontSize="24px" fontStyle="normal" fontWeight="700">
					    ${row?.plan?.price / 100} <span style={{fontSize: '17px', marginLeft: '-5px'}}>/{row?.plan?.paymentType === 'monthly' ? 'month' : 'year' }</span>
						</Text>

				    <Box mt="8px">
							<Text variant="title-lg" fontSize="15px">{ row?.canceledAt === null ? 'Next payment on' : 'Subscription Canceled'} </Text>
							
							{ row?.canceledAt === null && (row?.status === 'active' || row?.status === 'trialing') ? (
							    <Text variant="title-lg" fontSize="15px">{formatDateWithSuffix(row?.periodEnd)} for ${row?.plan?.price / 100}</Text>
							) : row?.canceledAt !== null && (row?.status === 'active' || row?.status === 'trialing') ? (		
						    	<Text variant="title-lg" fontSize="15px">Available until {formatDateWithSuffix(row?.periodEnd)}</Text>
							) : 
									<Text variant="title-lg" fontSize="15px">{ row?.periodEnd ? `End of period: ${formatDateWithSuffix(row?.periodEnd)}` : ''}</Text>
							}
						</Box>
					</Box>

					<Flex gap="12px">
						{ row?.status === 'active' ? 
							<Button 
								onClick={() => {
									setChangeStatusSubItem(row);
									onOpen();
								}}
		          	isLoading={isLoadingChangeStatusSub}
								zIndex="9" variant="solid-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
								Pause
							</Button>
							: row?.status === 'paused' ?
							<Button 
								onClick={() => {
									setChangeStatusSubItem(row);
									onOpen();
								}}
								isLoading={isLoadingChangeStatusSub}
								zIndex="9" variant="button-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
								Resume
							</Button>
							: <></>
						}

						{ row?.canceledAt === null &&
							<Button 
								onClick={() => {
									setChangeStatusSubItem(row);
									onOpenAttention();
								}}
								zIndex="9" variant="solid-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
								Cancel
							</Button>
						}

						<Button 
							onClick={() => goToCustomerPortal(row?.id)}
	          	isLoading={isLoadingCustomerPortal}
							zIndex="9" variant="solid-md" mt="12px" w={{base: '100%', sm: '222px', lg: '100%'}}>
							Details
						</Button>
					</Flex>
	      </Card>
	      ))}
	    </Grid>

	    <AttentionModal
        handle={changeStatusSub}
        isOpen={isOpen}
        onClose={onClose}
        title={ changeStatusSubItem?.status === 'active' 
	      	?	'Pause Membership'
	      	: 'Resume Membership'
	    	}
	      description={ changeStatusSubItem?.status === 'active'
	      	? 'You are pausing your subscription for 30 days. During this time, you won’t be charged or receive entries. You can return anytime, and it will automatically resume afterward with all your data saved.'
	      	: 'You are resuming your subscription.'
	      }/>

	    <AttentionModal
			  handle={handleCancelMembership}
			  isOpen={isOpenAttention}
			  onClose={onCloseAttention}
			  title="Cancel Subscription?"
			  description="Are you sure you want to cancel? Some features will no longer be available."
			/>

			<CancelSubscriptionModal
        isOpen={isOpenCancelAttention}
        onClose={onCloseCancelAttention}
        onOpen={onOpenCancelAttention} 
        confirmHandleCancelMembership={confirmHandleCancelMembership}
        changeStatusSubItem={changeStatusSubItem}
        activePlans={activePlans}
        status={status}
        userData={userData} />

	    { activePlans?.length === 0 && !isLoadingList && !isLoading && (
	    	<Box 
          textAlign="center" 
          py="40px" 
          maxW={{sm: '325px'}} 
          left="50%" 
          position="relative" 
          transform="translate(-50%, 0)">
            <EmptyListNotifications style={{margin: '0 auto'}} />
            <Text variant="title-md" fontSize="20px" mt="20px" pb="9px" letterSpacing="-0.4px">No subscriptions found</Text>
            <Text>Subscribe to get more features and rewards!</Text>
        </Box>
	    )}

	    { validateLoadMore(activePlans, activePlansMeta) && (
	        <Button variant="ghost-md"
	        	transform="translate(-50%, 0)"
	        	left="50%"
	        	maxW="220px" onClick={loadMore} m="24px auto !important" disabled={isLoading ? true : false} isLoading={isLoading}>
	          Load More
	        </Button>
	    )}
		</>
	);
};

export default Subscriptions;